import type { PmNodeConverter, PmNodeJson } from './../../pm-converters/types';
import {
  generateId,
  TIPTAP_CAPTION_TYPE,
  TIPTAP_FIGURE_TYPE,
  TIPTAP_IMAGE_TYPE,
} from './../../ricos-content';
import type { Decoration, Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { ImageNode } from './../../ricos-types';

export const imageConverter: PmNodeConverter = {
  toPm: {
    types: [Node_Type.IMAGE],
    // @ts-expect-error $TSFixMe
    convert: (node: ImageNode, visit: (node: Node) => PmNodeJson[]) => {
      const { containerData, caption: imageCaption, ...imageData } = node.imageData;

      const fallbackCaption: Node = {
        id: generateId(),
        type: Node_Type.CAPTION,
        nodes:
          typeof imageCaption === 'string' && imageCaption.length
            ? [
                {
                  type: Node_Type.TEXT,
                  id: '',
                  nodes: [],
                  textData: {
                    text: imageCaption,
                    decorations: [] as Decoration[],
                  },
                },
              ]
            : [],
      };

      const fallBackNodeWithCaption = { ...node, nodes: [fallbackCaption] };

      return {
        type: TIPTAP_FIGURE_TYPE,
        attrs: {
          id: generateId(),
        },
        content: [
          {
            type: TIPTAP_IMAGE_TYPE,
            attrs: {
              ...imageData,
              containerData,
              id: node.id,
              disableDownload: !!imageData.disableDownload,
              disableExpand: !!imageData.disableExpand,
              decorative: !!imageData.decorative,
            },
          },
          ...visit(node.nodes?.length > 0 ? node : fallBackNodeWithCaption),
        ],
      };
    },
  },
  fromPm: {
    types: [TIPTAP_FIGURE_TYPE, TIPTAP_IMAGE_TYPE],
    convert: (node, visit) => {
      if (node.type === TIPTAP_IMAGE_TYPE) {
        return undefined;
      }

      const imageNode = node.content?.find(({ type }) => type === TIPTAP_IMAGE_TYPE);

      if (!imageNode) {
        return undefined;
      }

      const { id, ...imageData } = imageNode?.attrs || {};

      const [captionNode] = visit?.(node) ?? [];
      const captionPlainText = captionToPlainText(node);

      const { spoiler, ...rest } = imageData.containerData || {};
      const containerData = {
        ...rest,
        ...(spoiler?.enabled ? { spoiler: { ...spoiler } } : {}),
      };

      return {
        type: Node_Type.IMAGE,
        id: id ?? generateId(),
        nodes: captionPlainText ? [captionNode] : [],
        imageData: {
          ...imageData,
          caption: captionPlainText,
          altText: imageData.altText || undefined,
          decorative: imageData.decorative || undefined,
          disableDownload: imageData.disableDownload || undefined,
          disableExpand: imageData.disableExpand || undefined,
          containerData: Object.keys(containerData).length ? containerData : undefined,
        },
      };
    },
  },
};

const captionToPlainText = (node: PmNodeJson): string | undefined => {
  const captionNode = node.content?.find(({ type }) => type === TIPTAP_CAPTION_TYPE) as PmNodeJson;

  return (
    captionNode?.content?.reduce((acc, { text }) => (text !== undefined ? acc + text : acc), '') ||
    undefined
  );
};
